import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormLabel,
  Input,
  Link,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatForDatetimeLocalInput, getOrganizationTimezone } from "../../lib/timezoneUtils";
import { apiClient } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { usePermission } from "../../hooks/usePermission";
import { ResponsiveTable } from "../common/ResponsiveTable";
import type {
  ProductionAssessment,
  ProductionConfirmation,
  ProductionHistory,
  ProductionMove,
  ProductionPolicy,
  ProductionQueue,
  ProductionReview,
  ProductionWeather,
  PrerequisiteKey,
} from "../../types/productionPlanner";

const labels: Record<PrerequisiteKey, string> = {
  scope: "Signed scope",
  deposit: "Cleared deposit",
  permit: "Permit evidence",
  materials: "Full material delivery",
  crew: "Crew suitability",
  training: "Required credentials",
  dependencies: "Prerequisite tasks",
};
const dateText = (value: string | null) =>
  value
    ? new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString()
    : "Not set";
const readable = (value: string) => value.replaceAll("_", " ");
function errorText(error: unknown): string {
  const e = error as { responseData?: unknown; message?: string };
  const flatten = (v: unknown): string =>
    typeof v === "string"
      ? v
      : Array.isArray(v)
        ? v.map(flatten).join(" ")
        : v && typeof v === "object"
          ? Object.entries(v)
              .map(([k, val]) => `${k}: ${flatten(val)}`)
              .join(" ")
          : "";
  return (
    flatten(e?.responseData) ||
    e?.message ||
    "The request could not be completed. Please try again."
  );
}
function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <FormLabel>{label}</FormLabel>
      {children}
    </Box>
  );
}
const grid = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0,1fr))" },
  gap: 1.5,
};

function History<T extends { note: string }>({
  rows,
}: {
  rows?: ProductionHistory<T>[];
}) {
  return (
    <Box component="details">
      <Typography component="summary" level="title-sm">
        Review history ({rows?.length || 0})
      </Typography>
      <Stack spacing={1} sx={{ mt: 1 }}>
        {rows?.map((row) => (
          <Box key={row.revision}>
            <Typography level="body-sm">
              Revision {row.revision} · {row.actor_name} ·{" "}
              {new Date(row.created_at).toLocaleString(undefined, { timeZone: getOrganizationTimezone() })}
            </Typography>
            <Typography level="body-sm">{row.data.note}</Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function ReviewForm({
  data,
  queue,
  onDone,
}: {
  data: ProductionAssessment;
  queue: ProductionQueue;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [base] = useState({
    expected_revision: data.plan.revision,
    expected_sources: data.source_digest,
  });
  const [form, setForm] = useState<ProductionReview>({
    policy: "default",
    manual_squares: null,
    manual_days: null,
    complexity: "1",
    confirmations: [],
    note: "",
    ...data.plan.data,
  });
  const save = useMutation({
    mutationFn: () =>
      apiClient.patch(`/api/scheduling/production/${data.job_id}/`, {
        ...form,
        ...base,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["production"] });
      onDone();
    },
  });
  const update = (
    key: PrerequisiteKey,
    patch: Partial<ProductionConfirmation>,
  ) =>
    setForm((old) => {
      const current = old.confirmations.find((c) => c.key === key) || {
        key,
        state: "pending",
        note: "",
        owner: null,
        expected_date: null,
        valid_through: null,
        document: null,
      };
      return {
        ...old,
        confirmations: [
          ...old.confirmations.filter((c) => c.key !== key),
          { ...current, ...patch },
        ],
      };
    });
  const policies = [
    { key: "default", name: "Company default" },
    ...queue.policies.filter((p) => p.key !== "default"),
  ];
  return (
    <Stack spacing={2} data-testid="production-review-form">
      <Typography level="title-lg">Review production readiness</Typography>
      <Alert color="neutral">
        Confirm only what you have reviewed. Partial delivery stays blocked.
        Overrides keep the reviewer, reason and expiry in the history. A source
        change requires another review.
      </Alert>
      <Box sx={grid}>
        <Field label="Production policy">
          <Select
            aria-label="Production policy"
            value={form.policy}
            onChange={(_, v) => setForm({ ...form, policy: v || "default" })}
          >
            {policies.map((p) => (
              <Option key={p.key} value={p.key}>
                {p.name}
              </Option>
            ))}
          </Select>
        </Field>
        <Field label="Reviewed roof size (squares)">
          <Input
            aria-label="Reviewed roof size (squares)"
            type="number"
            value={form.manual_squares || ""}
            onChange={(e) =>
              setForm({ ...form, manual_squares: e.target.value || null })
            }
          />
        </Field>
        <Field label="Reviewed duration (days)">
          <Input
            aria-label="Reviewed duration (days)"
            type="number"
            value={form.manual_days || ""}
            onChange={(e) =>
              setForm({
                ...form,
                manual_days: e.target.value ? Number(e.target.value) : null,
              })
            }
          />
        </Field>
        <Field label="Complexity multiplier">
          <Input
            aria-label="Complexity multiplier"
            type="number"
            value={form.complexity}
            onChange={(e) => setForm({ ...form, complexity: e.target.value })}
          />
        </Field>
      </Box>
      <Typography level="body-sm">
        A selected, completed ApexOS measurement supplies roof size before the
        manual fallback. Reviewed duration takes precedence over the
        production-rate estimate.
      </Typography>
      {data.checks.map((check) => {
        const current = form.confirmations.find((c) => c.key === check.key);
        return (
          <Card key={check.key} variant="outlined">
            <Stack
              direction="row"
              sx={{ gap: 1, flexWrap: "wrap", justifyContent: "space-between" }}
            >
              <Typography level="title-md">{check.title}</Typography>
              <Checkbox
                label={`Review ${check.title}`}
                checked={!!current}
                onChange={(e) =>
                  e.target.checked
                    ? update(check.key, {})
                    : setForm({
                        ...form,
                        confirmations: form.confirmations.filter(
                          (c) => c.key !== check.key,
                        ),
                      })
                }
              />
            </Stack>
            <Typography level="body-sm">{check.reason}</Typography>
            {current && (
              <>
                <Box sx={grid}>
                  <Field label="Review outcome">
                    <Select
                      aria-label={`${check.title} outcome`}
                      value={current.state}
                      onChange={(_, v) => v && update(check.key, { state: v })}
                    >
                      <Option value="pending">Pending</Option>
                      <Option value="partial">Partial / incomplete</Option>
                      {["scope", "permit", "materials"].includes(check.key) && (
                        <Option value="confirmed">Confirmed complete</Option>
                      )}
                      <Option value="override">Explicit override</Option>
                    </Select>
                  </Field>
                  <Field label="Responsible person">
                    <Select
                      aria-label={`${check.title} owner`}
                      value={current.owner || 0}
                      onChange={(_, v) =>
                        update(check.key, { owner: v || null })
                      }
                    >
                      <Option value={0}>Unassigned</Option>
                      {queue.people.map((p) => (
                        <Option key={p.id} value={p.id}>
                          {p.name}
                        </Option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Expected ready date">
                    <Input
                      aria-label={`${check.title} expected date`}
                      type="date"
                      value={current.expected_date || ""}
                      onChange={(e) =>
                        update(check.key, {
                          expected_date: e.target.value || null,
                        })
                      }
                    />
                  </Field>
                  <Field label="Valid through planned work">
                    <Input
                      aria-label={`${check.title} valid through`}
                      type="date"
                      value={current.valid_through || ""}
                      onChange={(e) =>
                        update(check.key, {
                          valid_through: e.target.value || null,
                        })
                      }
                    />
                  </Field>
                  <Field label="Supporting job document">
                    <Select
                      aria-label={`${check.title} document`}
                      value={current.document || 0}
                      onChange={(_, v) =>
                        update(check.key, { document: v || null })
                      }
                    >
                      <Option value={0}>
                        Manual confirmation (explain below)
                      </Option>
                      {data.records.documents.map((d) => (
                        <Option key={d.id} value={d.id}>
                          {d.name || `Document ${d.id}`}
                        </Option>
                      ))}
                    </Select>
                  </Field>
                </Box>
                <Field label="Evidence or override reason">
                  <Textarea
                    aria-label={`${check.title} reason`}
                    minRows={2}
                    value={current.note}
                    onChange={(e) =>
                      update(check.key, { note: e.target.value })
                    }
                  />
                </Field>
              </>
            )}
          </Card>
        );
      })}
      <Field label="Approval note">
        <Textarea
          aria-label="Production approval note"
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
        />
      </Field>
      {save.isError && <Alert color="danger">{errorText(save.error)}</Alert>}
      <Stack direction="row" spacing={1}>
        <Button loading={save.isPending} onClick={() => save.mutate()}>
          Approve Production Review
        </Button>
        <Button variant="plain" onClick={onDone}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
}

function MoveForm({
  job,
  onDone,
}: {
  job: ProductionAssessment;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    date: job.build_date || "",
    reason: "",
    move_tasks: true,
    move_work_orders: true,
    move_appointments: true,
  });
  const [ack, setAck] = useState(false);
  const preview = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post<ProductionMove>(
          `/api/scheduling/production/${job.job_id}/move-preview/`,
          form,
        )
      ).data,
  });
  const commit = useMutation({
    mutationFn: () =>
      apiClient.post(`/api/scheduling/production/${job.job_id}/move-commit/`, {
        token: preview.data?.token,
        acknowledge: ack,
      }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["production"] }),
        qc.invalidateQueries({ queryKey: ["dispatch"] }),
      ]);
      onDone();
    },
  });
  const change = (patch: Partial<typeof form>) => {
    setForm({ ...form, ...patch });
    preview.reset();
    commit.reset();
    setAck(false);
  };
  return (
    <Card data-testid="production-move">
      <Typography level="title-lg">Review a schedule move</Typography>
      <Box sx={grid}>
        <Field label="New build date">
          <Input
            type="date"
            aria-label="New build date"
            value={form.date}
            onChange={(e) => change({ date: e.target.value })}
          />
        </Field>
        <Field label="Reason for moving">
          <Input
            aria-label="Reason for moving"
            value={form.reason}
            onChange={(e) => change({ reason: e.target.value })}
          />
        </Field>
      </Box>
      <Stack sx={{ gap: 1 }}>
        {(
          [
            ["move_tasks", "Move linked task dates"],
            ["move_work_orders", "Revise unfinished work order dates"],
            ["move_appointments", "Move linked maintenance appointments"],
          ] as const
        ).map(([key, label]) => (
          <Checkbox
            key={key}
            label={label}
            checked={form[key]}
            onChange={(e) => change({ [key]: e.target.checked })}
          />
        ))}
      </Stack>
      <Typography level="body-sm">
        Only appointments linked through this job’s originating proposal are
        included. Completed and in-progress records remain unchanged. Supplier
        dates and external calendar invitations require separate follow-up.
      </Typography>
      <Button
        variant="outlined"
        loading={preview.isPending}
        onClick={() => {
          setAck(false);
          preview.mutate();
        }}
      >
        Preview Date Changes
      </Button>
      {preview.isError && (
        <Alert color="danger">{errorText(preview.error)}</Alert>
      )}
      {preview.data && (
        <>
          <Alert color="warning">{preview.data.note}</Alert>
          <ResponsiveTable
            rows={preview.data.rows}
            getRowKey={(r) => `${r.type}:${r.id}`}
            columns={[
              {
                key: "label",
                header: "Record",
                mobileVariant: "header",
                render: (r) => (
                  <Typography>
                    {r.label}
                    <Typography level="body-xs">{readable(r.type)}</Typography>
                  </Typography>
                ),
              },
              {
                key: "dates",
                header: "Date changes",
                render: (r) => (
                  <Stack>
                    {Object.entries(r.after)
                      .filter(([k, v]) => v !== r.before[k])
                      .map(([k, v]) => (
                        <Typography level="body-sm" key={k}>
                          {readable(k)}: {r.before[k] || "not set"} → {v}
                        </Typography>
                      ))}
                  </Stack>
                ),
              },
              {
                key: "notices",
                header: "Review",
                render: (r) => (
                  <Stack>
                    {r.conflicts.map((c) => (
                      <Typography color="warning" key={c}>
                        {c}
                      </Typography>
                    ))}
                    <Typography level="body-sm">{r.notice}</Typography>
                  </Stack>
                ),
              },
            ]}
          />
          {preview.data.blockers.map((b) => (
            <Alert key={b.key} color="warning">
              {b.title}: {b.reason}
            </Alert>
          ))}
          <Typography level="body-sm">
            {preview.data.unchanged.length} linked records excluded by your
            choices.
          </Typography>
          <Checkbox
            label="I reviewed the changes, blockers, conflicts and follow-up notices"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
          />
          <Button
            loading={commit.isPending}
            disabled={!ack}
            onClick={() => commit.mutate()}
          >
            Apply Reviewed Move
          </Button>
          {commit.isError && (
            <Alert color="danger">{errorText(commit.error)}</Alert>
          )}
        </>
      )}
      <Button variant="plain" onClick={onDone}>
        Close Schedule Review
      </Button>
    </Card>
  );
}

function JobReadiness({
  jobId,
  queue,
}: {
  jobId: number;
  queue: ProductionQueue;
}) {
  const { can } = usePermission();
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [moving, setMoving] = useState(false);
  const [showOptional, setShowOptional] = useState(false);
  const query = useQuery({
    queryKey: ["production", user?.organization, "job", jobId],
    queryFn: async () =>
      (
        await apiClient.get<ProductionAssessment>(
          `/api/scheduling/production/${jobId}/`,
        )
      ).data,
  });
  const weather = useMutation({
    mutationFn: async () =>
      (
        await apiClient.get<ProductionWeather>(
          `/api/scheduling/production/${jobId}/weather/`,
        )
      ).data,
  });
  if (query.isLoading)
    return <Typography>Loading production checks…</Typography>;
  if (query.isError)
    return (
      <Alert color="danger">
        {errorText(query.error)}
        <Button onClick={() => query.refetch()}>Retry</Button>
      </Alert>
    );
  const data = query.data;
  if (!data) return null;
  return (
    <Card data-testid="production-job">
      <Stack
        direction="row"
        sx={{
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography level="h3">{data.name}</Typography>
          <Link component={RouterLink} to={`/operations/jobs/${jobId}`}>
            Open job · {data.job_number}
          </Link>
        </Box>
        <Chip color={data.state === "ready" ? "success" : "warning"}>
          {data.state === "ready"
            ? "Prerequisites ready"
            : `${data.blockers} blocker(s)`}
        </Chip>
      </Stack>
      <Typography>
        Planned start: {dateText(data.build_date)} · Estimated earliest start:{" "}
        {dateText(data.earliest_date)}
        {data.earliest_is_estimate
          ? " (pending prerequisite confirmations)"
          : ""}
      </Typography>
      <Typography level="body-sm">
        {data.policy.name} ·{" "}
        {data.capacity.days
          ? `${data.capacity.days} day(s)`
          : "Duration unknown"}{" "}
        · {data.capacity.squares || "Unknown"} squares · {data.capacity.source}{" "}
        · {data.capacity.rate} squares/day × complexity{" "}
        {data.capacity.complexity}. Calendar days; review weekends, weather and
        crew availability.
      </Typography>
      {!editing && (
        <>
          <ResponsiveTable
            rows={data.checks.filter(
              (check) => showOptional || check.state !== "not_required",
            )}
            getRowKey={(r) => r.key}
            columns={[
              {
                key: "title",
                header: "Prerequisite",
                mobileVariant: "header",
                render: (r) => r.title,
              },
              {
                key: "state",
                header: "Status",
                render: (r) => (
                  <Chip
                    size="sm"
                    color={
                      r.state === "blocked"
                        ? "warning"
                        : r.state === "ready"
                          ? "success"
                          : "neutral"
                    }
                  >
                    {readable(r.state)}
                  </Chip>
                ),
              },
              {
                key: "reason",
                header: "Evidence / next action",
                mobileVariant: "body",
                render: (r) => (
                  <Box>
                    <Typography level="body-sm">{r.reason}</Typography>
                    <Link
                      component={RouterLink}
                      to={
                        r.key === "training"
                          ? "/hr/training"
                          : `/operations/jobs/${jobId}?tab=${({ scope: "documents", permit: "documents", deposit: "billing", materials: "material-orders", crew: "work-orders", dependencies: "tasks" } as Record<string, string>)[r.key]}`
                      }
                    >
                      Review supporting records
                    </Link>
                    {r.confirmation?.actor && (
                      <Typography level="body-xs">
                        Reviewed by {r.confirmation.actor} · valid through{" "}
                        {dateText(r.confirmation.valid_through)}
                      </Typography>
                    )}
                  </Box>
                ),
              },
              {
                key: "owner",
                header: "Owner / expected",
                render: (r) => (
                  <Box>
                    {r.owner}
                    <Typography level="body-sm">
                      {dateText(r.expected_date)}
                    </Typography>
                  </Box>
                ),
              },
            ]}
          />
          <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
            {can("scheduling", "edit") && can("jobs", "edit") && (
              <Button onClick={() => setEditing(true)}>
                Review Prerequisites
              </Button>
            )}
            {can("scheduling", "edit") && !!data.build_date && (
              <Button variant="outlined" onClick={() => setMoving(!moving)}>
                Reschedule With Preview
              </Button>
            )}
            <Button
              variant="outlined"
              loading={weather.isPending}
              onClick={() => weather.mutate()}
            >
              Check Build Weather
            </Button>
          </Stack>
          {data.checks.some((check) => check.state === "not_required") && (
            <Button
              variant="plain"
              size="sm"
              onClick={() => setShowOptional(!showOptional)}
            >
              {showOptional
                ? "Hide Optional Checks"
                : `Show ${data.checks.filter((check) => check.state === "not_required").length} Checks Not Required By This Policy`}
            </Button>
          )}
          <History rows={data.plan.history} />
        </>
      )}
      {editing && (
        <ReviewForm
          data={data}
          queue={queue}
          onDone={() => setEditing(false)}
        />
      )}
      {moving && <MoveForm job={data} onDone={() => setMoving(false)} />}
      {weather.isError && (
        <Alert color="warning">{errorText(weather.error)}</Alert>
      )}
      {weather.data && (
        <Card variant="outlined">
          <Typography level="title-md">
            {weather.data.source} · {readable(weather.data.state)}
          </Typography>
          <Typography level="body-sm">
            Issued:{" "}
            {weather.data.issued_at
              ? new Date(weather.data.issued_at).toLocaleString(undefined, { timeZone: getOrganizationTimezone() })
              : "Unavailable"}{" "}
            · Checked: {new Date(weather.data.retrieved_at).toLocaleString(undefined, { timeZone: getOrganizationTimezone() })}
          </Typography>
          <Typography level="body-sm">{weather.data.reason}</Typography>
          {weather.data.days.map((d) => (
            <Typography key={d.date}>
              {dateText(d.date)}: {readable(d.state)}
              {d.rain_percent != null
                ? ` · precipitation ${d.rain_percent}%`
                : ""}
              {d.wind_mph != null ? ` · wind ${d.wind_mph} mph` : ""}
              {d.reason ? ` · ${d.reason}` : ""}
            </Typography>
          ))}
        </Card>
      )}
    </Card>
  );
}

function PolicyEditor({
  queue,
  onClose,
}: {
  queue: ProductionQueue;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [key, setKey] = useState("default");
  const [newKey, setNewKey] = useState("");
  const [draft, setDraft] = useState<
    (ProductionPolicy & { expected_revision: number }) | null
  >(null);
  const policy = useQuery({
    queryKey: ["production", "policy", key],
    queryFn: async () =>
      (
        await apiClient.get<{
          data: ProductionPolicy;
          revision: number;
          history: ProductionHistory<ProductionPolicy & { note: string }>[];
        }>(`/api/scheduling/production/policies/${key}/`)
      ).data,
  });
  const save = useMutation({
    mutationFn: () =>
      apiClient.patch(`/api/scheduling/production/policies/${key}/`, draft),
    onSuccess: async () => {
      setDraft(null);
      await qc.invalidateQueries({ queryKey: ["production"] });
    },
  });
  const data =
    draft ||
    (policy.data
      ? { ...policy.data.data, expected_revision: policy.data.revision }
      : null);
  const update = (patch: Partial<ProductionPolicy>) =>
    data && setDraft({ ...data, ...patch });
  return (
    <Card data-testid="production-policy">
      <Typography level="h3">Company production policies</Typography>
      <Typography level="body-sm">
        Create named policies for different job types, then choose one in the
        job’s production review.
      </Typography>
      <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
        <Select
          aria-label="Policy to edit"
          value={key}
          onChange={(_, v) => {
            if (v) {
              setKey(v);
              setDraft(null);
              save.reset();
            }
          }}
        >
          <Option value="default">Company default</Option>
          {queue.policies
            .filter((p) => p.key !== "default")
            .map((p) => (
              <Option key={p.key} value={p.key}>
                {p.name}
              </Option>
            ))}
          {key !== "default" && !queue.policies.some((p) => p.key === key) && (
            <Option value={key}>{key}</Option>
          )}
        </Select>
        <Input
          aria-label="New policy identifier"
          placeholder="e.g. insurance-replacement"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
        />
        <Button
          variant="outlined"
          disabled={!/^[a-zA-Z0-9_-]{1,60}$/.test(newKey)}
          onClick={() => {
            setKey(newKey);
            setDraft(null);
            save.reset();
          }}
        >
          Create Policy
        </Button>
      </Stack>
      {policy.isError && (
        <Alert color="danger">{errorText(policy.error)}</Alert>
      )}
      {data && (
        <>
          <Box sx={grid}>
            <Field label="Policy name">
              <Input
                aria-label="Policy name"
                value={data.name}
                onChange={(e) => update({ name: e.target.value })}
              />
            </Field>
            <Field label="Required deposit (%)">
              <Input
                aria-label="Required deposit (%)"
                type="number"
                value={data.deposit_percent}
                onChange={(e) => update({ deposit_percent: e.target.value })}
              />
            </Field>
            <Field label="Crew production rate (squares/day)">
              <Input
                aria-label="Crew production rate (squares/day)"
                type="number"
                value={data.squares_per_day}
                onChange={(e) => update({ squares_per_day: e.target.value })}
              />
            </Field>
            <Field label="Rain review threshold (%)">
              <Input
                aria-label="Rain review threshold (%)"
                type="number"
                value={data.rain_percent}
                onChange={(e) =>
                  update({ rain_percent: Number(e.target.value) })
                }
              />
            </Field>
            <Field label="Wind review threshold (mph)">
              <Input
                aria-label="Wind review threshold (mph)"
                type="number"
                value={data.wind_mph}
                onChange={(e) => update({ wind_mph: Number(e.target.value) })}
              />
            </Field>
            <Field label="Credential requirements">
              <Select
                aria-label="Credential requirements"
                value={data.credential_set || ""}
                onChange={(_, v) => update({ credential_set: v || null })}
              >
                <Option value="">
                  Choose an existing training requirement set
                </Option>
                {queue.credential_sets.map((r) => (
                  <Option key={r.id} value={r.id}>
                    {r.name}
                  </Option>
                ))}
              </Select>
            </Field>
          </Box>
          <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
            {Object.entries(labels).map(([k, label]) => (
              <Checkbox
                key={k}
                label={`Require ${label}`}
                checked={data.required.includes(k as PrerequisiteKey)}
                onChange={(e) =>
                  update({
                    required: e.target.checked
                      ? [...data.required, k as PrerequisiteKey]
                      : data.required.filter((v) => v !== k),
                  })
                }
              />
            ))}
          </Stack>
          <Field label="Policy approval reason">
            <Textarea
              aria-label="Policy approval reason"
              value={data.note || ""}
              onChange={(e) => update({ note: e.target.value })}
            />
          </Field>
          <Button
            disabled={!draft}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Approve Production Policy
          </Button>
          {save.isError && (
            <Alert color="danger">{errorText(save.error)}</Alert>
          )}
          {save.isSuccess && (
            <Alert color="success">Production policy approved.</Alert>
          )}
          <History rows={policy.data?.history} />
        </>
      )}
      <Button variant="plain" onClick={onClose}>
        Close Policies
      </Button>
    </Card>
  );
}

export function ProductionPlanner() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [policyOpen, setPolicyOpen] = useState(false);
  const [search, setSearch] = useState(params.get("search") || "");
  const jobId = Number(params.get("job")) || 0;
  const update = (values: Record<string, string>) => {
    const next = new URLSearchParams(params);
    Object.entries(values).forEach(([key, value]) => next.set(key, value));
    setParams(next);
  };
  const today = formatForDatetimeLocalInput(new Date(), getOrganizationTimezone()).slice(0, 10);
  const start = params.get("date") || today;
  const page = Number(params.get("page")) || 1;
  const period = params.get("period") || "upcoming";
  const query = useQuery({
    queryKey: [
      "production",
      user?.organization,
      "queue",
      start,
      page,
      period,
      params.get("search"),
    ],
    queryFn: async () =>
      (
        await apiClient.get<ProductionQueue>(
          `/api/scheduling/production/?date=${start}&page=${page}&period=${period}&search=${encodeURIComponent(params.get("search") || "")}`,
        )
      ).data,
  });
  return (
    <Stack spacing={2} data-testid="production-planner">
      <Card>
        <Stack
          direction="row"
          sx={{ gap: 1, flexWrap: "wrap", justifyContent: "space-between" }}
        >
          <Box>
            <Typography level="h2">Production readiness</Typography>
            <Typography>
              Know what is ready to build, who owns each blocker and which crews
              are committed.
            </Typography>
          </Box>
          {(user?.is_organization_admin || user?.is_superuser) && (
            <Button
              variant="outlined"
              onClick={() => setPolicyOpen(!policyOpen)}
            >
              Production Policies
            </Button>
          )}
        </Stack>
        <Box sx={grid}>
          <Field label="Planning window starts">
            <Input
              aria-label="Planning window starts"
              type="date"
              value={start}
              onChange={(e) => update({ date: e.target.value, page: "1" })}
            />
          </Field>
          <Field label="Jobs to include">
            <Select
              aria-label="Jobs to include"
              value={period}
              onChange={(_, v) =>
                update({ period: v || "upcoming", page: "1" })
              }
            >
              <Option value="upcoming">Next 14 days and unscheduled</Option>
              <Option value="all">All unfinished jobs</Option>
            </Select>
          </Field>
          <Field label="Find a job">
            <Stack direction="row" spacing={1}>
              <Input
                sx={{ minWidth: 0, flex: 1 }}
                aria-label="Find a production job"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && update({ search, page: "1" })
                }
              />
              <Button onClick={() => update({ search, page: "1" })}>
                Search
              </Button>
            </Stack>
          </Field>
        </Box>
      </Card>
      {query.isLoading && (
        <Typography>Checking production prerequisites…</Typography>
      )}
      {query.isError && (
        <Alert color="danger">
          {errorText(query.error)}
          <Button onClick={() => query.refetch()}>Retry</Button>
        </Alert>
      )}
      {query.data && (
        <>
          {policyOpen && (
            <PolicyEditor
              queue={query.data}
              onClose={() => setPolicyOpen(false)}
            />
          )}
          <Card>
            <ResponsiveTable
              rows={query.data.results}
              getRowKey={(r) => r.job_id}
              columns={[
                {
                  key: "job",
                  header: "Job",
                  mobileVariant: "header",
                  render: (r) => (
                    <Button
                      variant="plain"
                      sx={{ whiteSpace: "normal", textAlign: "left" }}
                      onClick={() => update({ job: String(r.job_id) })}
                    >
                      {r.name}
                    </Button>
                  ),
                },
                {
                  key: "state",
                  header: "Readiness",
                  render: (r) => (
                    <Chip color={r.state === "ready" ? "success" : "warning"}>
                      {r.state === "ready" ? "Ready" : `${r.blockers} blockers`}
                    </Chip>
                  ),
                },
                {
                  key: "date",
                  header: "Planned / earliest",
                  render: (r) => (
                    <Box>
                      {dateText(r.build_date)}
                      <Typography level="body-sm">
                        Earliest: {dateText(r.earliest_date)}
                      </Typography>
                    </Box>
                  ),
                },
                {
                  key: "capacity",
                  header: "Roof / duration",
                  render: (r) => (
                    <Box>
                      {r.capacity.squares || "Unknown"} squares
                      <Typography level="body-sm">
                        {r.capacity.days
                          ? `${r.capacity.days} day(s)`
                          : "Review duration"}
                      </Typography>
                    </Box>
                  ),
                },
                {
                  key: "blockers",
                  header: "Next action",
                  mobileVariant: "body",
                  render: (r) => (
                    <Typography level="body-sm">
                      {r.checks
                        .filter((c) => c.state === "blocked")
                        .map((c) => `${c.title} · ${c.owner}`)
                        .join("; ") ||
                        "Prerequisites reviewed; check weather and availability"}
                    </Typography>
                  ),
                },
              ]}
            />
            {!query.data.count && (
              <Typography>No unfinished jobs in this view.</Typography>
            )}
            <Stack
              direction="row"
              sx={{ gap: 1, alignItems: "center", flexWrap: "wrap" }}
            >
              <Button
                variant="outlined"
                disabled={page <= 1}
                onClick={() => update({ page: String(page - 1) })}
              >
                Previous
              </Button>
              <Typography level="body-sm">
                Page {page} · {query.data.count} jobs
              </Typography>
              <Button
                variant="outlined"
                disabled={page * 20 >= query.data.count}
                onClick={() => update({ page: String(page + 1) })}
              >
                Next
              </Button>
            </Stack>
          </Card>
          {jobId > 0 && (
            <JobReadiness key={jobId} jobId={jobId} queue={query.data} />
          )}
          <Card>
            <Typography level="h3">Crew capacity · next 14 days</Typography>
            <Typography level="body-sm">{query.data.capacity_note}</Typography>
            <ResponsiveTable
              rows={query.data.crew_days}
              getRowKey={(r) => `${r.crew_id}:${r.date}`}
              pagination={{ pageSize: 14, itemNoun: "crew days" }}
              columns={[
                {
                  key: "crew",
                  header: "Crew",
                  mobileVariant: "header",
                  render: (r) => r.crew,
                },
                { key: "date", header: "Day", render: (r) => dateText(r.date) },
                {
                  key: "work",
                  header: "Reserved work",
                  render: (r) => r.orders.join(", "),
                },
                {
                  key: "status",
                  header: "Capacity",
                  render: (r) => (
                    <Chip color={r.over_capacity ? "warning" : "neutral"}>
                      {r.jobs.length} job(s)
                      {r.over_capacity ? " · review overlap" : ""}
                    </Chip>
                  ),
                },
              ]}
            />
            {!query.data.crew_days.length && (
              <Typography>
                No crew work orders scheduled in this window.
              </Typography>
            )}
          </Card>
        </>
      )}
    </Stack>
  );
}
